<template>
  <form class="verify-user-form" @submit.prevent="handleSubmit">
    <FriedInput
      v-model.trim="profileInformation.firstName"
      :autofocus="!profileInformation.firstName"
      data-external="signup-verify-user-first-name-input"
      :label="$t('first-name')"
      autocomplete="first-name"
      :error-message="validation.firstName"
    />
    <FriedInput
      v-model.trim="profileInformation.lastName"
      :autofocus="profileInformation.firstName && !profileInformation.lastName"
      data-external="signup-verify-user-last-name-input"
      :label="$t('last-name')"
      autocomplete="family-name"
      :error-message="validation.lastName"
    />
    <FriedSelect
      :options="languages"
      data-external="signup-verify-user-language-dropdown"
      :append-to-body="true"
      :model-value="languageObject"
      option-label="label"
      :label="$t('language')"
      :empty-state-text="$t('no-items-found')"
      :error-message="validation.language"
      @update:model-value="handleLanguageChange"
    />
    <FriedInput
      :model-value="unFormattedPhoneNumber"
      data-external="signup-verify-user-phone-number-input"
      type="tel"
      autocomplete="off"
      :label="phoneNumberLabel"
      placeholder="+123456789"
      :error-message="validation.phoneNumber"
      @update:model-value="handlePhoneNumberInput"
    />
    <HiddenInput autocomplete="email" />
    <FriedInput
      v-model="profileInformation.password"
      :autofocus="!!profileInformation.firstName && !!profileInformation.lastName"
      data-external="signup-verify-user-password-input"
      :type="passwordInputType"
      :label="$t('password')"
      autocomplete="new-password"
      :error-message="validation.password"
      :help-message="$t('password-must-include')"
      @focus="handlePasswordFocus"
      @blur="handlePasswordBlur"
    >
      <template #append>
        <button
          type="button"
          tabindex="-1"
          class="view-password-button"
          data-external="signup-verify-user-view-password-button"
          :title="$t('show-and-hide-password')"
          @click.prevent="toggleViewPassword"
        >
          <FriedIcon v-if="!viewPassword" key="1" aria-hidden icon="view" />
          <FriedIcon v-else key="2" aria-hidden icon="view-hide" />
        </button>
      </template>
    </FriedInput>
    <PasswordValidator
      :input="profileInformation.password"
      class="password-validator"
      :show-errors="showValidatorError"
      @is-valid-password="handleIsValidPassword"
    />
    <FriedButton
      type="submit"
      data-external="signup-verify-user-submit-button"
      :loading="loading"
      class="submit-button"
    >
      {{ $t('continue') }}
    </FriedButton>
  </form>
</template>

<script lang="ts">
import type { PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { InputType } from '@getaccept/fried-tofu';
import PasswordValidator from '@getaccept/lib-shared-new/src/password-validator/PasswordValidator.vue';
import HiddenInput from '@getaccept/lib-shared-new/src/hidden-input/HiddenInput.vue';
import type { Language } from '@getaccept/lib-shared-new/src/languages/types/language';
import { languages } from '@getaccept/lib-shared-new/src/languages/constants/language';
import type { Ref } from 'vue';
import { ref, onMounted, computed, defineComponent, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ProfileInformation } from '../types/profile-information';
import { PasswordValidation } from '../../helpers/password-validation';

export default defineComponent({
  name: 'VerifyUserForm',
  components: {
    PasswordValidator,
    HiddenInput,
  },
  props: {
    loading: { type: Boolean, default: false },
    defaultLanguage: { type: String, default: '' },
    defaultFirstName: { type: String, default: '' },
    defaultLastName: { type: String, default: '' },
    isInvitation: { type: Boolean, default: false },
  },
  emits: ['language-change', 'submit'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const unFormattedPhoneNumber = ref('');
    const viewPassword = ref(false);
    const profileInformation: Ref<ProfileInformation> = ref({
      firstName: '',
      lastName: '',
      language: '',
      phoneNumber: '',
      password: '',
    });
    const passwordFocused = ref(false);
    const showValidatorError = ref(false);
    const isValidPassword = ref(false);
    const validation: Ref<ProfileInformation> = ref({
      firstName: '',
      lastName: '',
      language: '',
      phoneNumber: '',
      password: '',
    });

    watch(
      () => profileInformation.value.password,
      () => (validation.value.password = '')
    );

    watch(
      () => profileInformation.value.firstName,
      () => (validation.value.firstName = '')
    );

    watch(
      () => profileInformation.value.lastName,
      () => (validation.value.lastName = '')
    );

    watch(
      () => profileInformation.value.phoneNumber,
      () => (validation.value.phoneNumber = '')
    );

    const passwordInputType = computed(() =>
      viewPassword.value ? InputType.Text : InputType.Password
    );

    const languageObject = computed(() =>
      languages.find(language => language.key === profileInformation.value.language)
    );

    const formHasError = computed(() =>
      Object.entries(validation.value)
        .map(entry => entry[1])
        .some(value => !!value)
    );

    const phoneNumberLabel = computed(() =>
      props.isInvitation ? t('phone-number-optional') : t('phone-number')
    );

    const toggleViewPassword = () => (viewPassword.value = !viewPassword.value);

    const handlePhoneNumberInput = (value: string) => {
      unFormattedPhoneNumber.value = value;
      const phoneNumber: PhoneNumber = parsePhoneNumberFromString(unFormattedPhoneNumber.value);
      profileInformation.value.phoneNumber = phoneNumber?.number.toString() || value;
    };

    const handlePasswordFocus = () => (passwordFocused.value = true);

    const handlePasswordBlur = () => (passwordFocused.value = false);

    const handleLanguageChange = (language: Language) => {
      profileInformation.value.language = language.key;
      emit('language-change', language);
    };

    const handleIsValidPassword = (isValid: boolean) => (isValidPassword.value = isValid);

    const validatePhoneNumber = (number: string): string => {
      if (props.isInvitation) return '';
      const numberRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g;
      if (!numberRegex.test(number)) {
        return t('enter-a-valid-phone-number').toString();
      }
      return '';
    };

    const validateForm = ({ password, firstName, lastName, phoneNumber }): ProfileInformation => {
      const passwordError: string = PasswordValidation.validateNewPassword(
        password,
        isValidPassword.value
      );
      const phoneNumberError: string = validatePhoneNumber(phoneNumber);

      return {
        password: passwordError,
        firstName: firstName ? '' : t('first-name-is-required').toString(),
        lastName: lastName ? '' : t('last-name-is-required').toString(),
        phoneNumber: phoneNumberError,
        language: '',
      };
    };

    const handleSubmit = () => {
      showValidatorError.value = true;
      validation.value = validateForm(profileInformation.value);

      if (formHasError.value) {
        return;
      }

      emit('submit', profileInformation.value);
    };

    onMounted(() => {
      profileInformation.value.firstName = props.defaultFirstName;
      profileInformation.value.lastName = props.defaultLastName;
      profileInformation.value.language = props.defaultLanguage;
    });

    return {
      unFormattedPhoneNumber,
      viewPassword,
      profileInformation,
      passwordFocused,
      showValidatorError,
      isValidPassword,
      validation,
      validatePhoneNumber,
      onMounted,
      passwordInputType,
      languageObject,
      formHasError,
      toggleViewPassword,
      handlePhoneNumberInput,
      handlePasswordFocus,
      handlePasswordBlur,
      handleLanguageChange,
      handleIsValidPassword,
      validateForm,
      handleSubmit,
      phoneNumberLabel,
      languages,
    };
  },
});
</script>

<style lang="scss" scoped>
.verify-user-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);

  :deep(.input-box, .floater-reference-container) {
    width: 100%;
  }
}

.submit-button.button {
  margin-top: var(--spacing-100);
  width: 100%;
  margin-bottom: var(--spacing-150);
}

.password-validator {
  margin-top: var(--spacing-50);
}

.view-password-button {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  height: 2.5rem;
  width: 2.5rem;
}
</style>
